import React, { FunctionComponent } from 'react';
import styles from './TranslationHeaderSummary.Styles';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { TooltipTextField } from '../../../../../generic-components/tooltip-textfield/TooltipTextField.Component';
import ServiceTypeRequestTranslationEntity from '../../../../../../entities/ServiceTypeRequestTranslation.Entity';
import AppointmentTypeEntity from '../../../../../../entities/AppointmentType.Entity';
import Moment from 'moment';
import InterpretationTypeEntity from '../../../../../../entities/InterpretationType.Entity';
import LanguageEntity from '../../../../../../entities/Language.Entity';
import { TT_AUTHORIZATION_NOTES_LABEL } from '../../../../referral-add/referral-service/TtServiceConstants';

const SHOW_LEGACY_TRANSLATION_REQUEST_FIELDS = false;

interface ITranslationHeaderSummaryProps {
    serviceTypeRequestTranslation: ServiceTypeRequestTranslationEntity;
    appointmentTypes: Array<AppointmentTypeEntity>;
    interpretationTypes: Array<InterpretationTypeEntity>;
    languages: Array<LanguageEntity>;
}

export const TranslationHeaderSummary: FunctionComponent<ITranslationHeaderSummaryProps> = (
    props: ITranslationHeaderSummaryProps,
) => {
    const classes = styles();
    const translation = props.serviceTypeRequestTranslation;

    return (
        <Accordion defaultExpanded={true} className={classes.paper}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                aria-controls="panel1a-content"
                id="cliam-accordion"
                className={classes.paperHeader}
            >
                <Typography variant="subtitle2" className={classes.sidebarTitle}>
                    Translation Service
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={3} className={classes.gridContainer}>
                    <Grid item xs={4}>
                        <TooltipTextField
                            type="typography"
                            label="Appointment Type"
                            typographyLabelClass={classes.dataLabel}
                            typographyValueClass={classes.blackFont}
                            value={
                                translation?.appointmentTypeId === 0
                                    ? 'Other'
                                    : props.appointmentTypes?.find(
                                          (x) => x.appointmentTypeId === translation?.appointmentTypeId,
                                      )?.appointmentTypeName ?? 'Other'
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TooltipTextField
                            type="typography"
                            label="Other Appointment Type"
                            typographyLabelClass={classes.dataLabel}
                            typographyValueClass={classes.blackFont}
                            value={
                                translation?.appointmentTypeId === 0 ? translation?.otherAppointmentType : '-'
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TooltipTextField
                            type="typography"
                            label="Appointment Date"
                            typographyLabelClass={classes.dataLabel}
                            typographyValueClass={classes.blackFont}
                            value={Moment(translation?.appointmentDate).format('MM/DD/YYYY')}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <TooltipTextField
                            type="typography"
                            label="Frequency and Duration"
                            typographyLabelClass={classes.dataLabel}
                            typographyValueClass={classes.blackFont}
                            value={translation?.frequencyAndDuration ? translation.frequencyAndDuration : '-'}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TooltipTextField
                            type="typography"
                            label="Language"
                            typographyLabelClass={classes.dataLabel}
                            typographyValueClass={classes.blackFont}
                            value={
                                translation?.languageId === 0
                                    ? 'Other'
                                    : props.languages?.find((x) => x.languageId === translation?.languageId)
                                          ?.languageName ?? 'Other'
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TooltipTextField
                            type="typography"
                            label="Other Language"
                            typographyLabelClass={classes.dataLabel}
                            typographyValueClass={classes.blackFont}
                            value={translation?.languageId === 0 ? translation?.otherLanguage : '-'}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <Typography variant="body2" className={classes.dataLabel}>
                            Rush Request
                        </Typography>
                        <Typography variant="subtitle2" className={classes.blackFont}>
                            {translation?.rushRequest ? 'Yes' : 'No'}
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <TooltipTextField
                            type="typography"
                            label={TT_AUTHORIZATION_NOTES_LABEL}
                            typographyLabelClass={classes.dataLabel}
                            typographyValueClass={classes.blackFont}
                            value={translation?.specialInstructions ? translation.specialInstructions : '-'}
                        />
                    </Grid>

                    {SHOW_LEGACY_TRANSLATION_REQUEST_FIELDS && (
                        <>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    label="Appointment Time"
                                    id="appointmentTime"
                                    type="typography"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={
                                        typeof translation?.appointmentTime === 'string'
                                            ? translation?.appointmentTime?.split('T')[1].substring(0, 5)
                                            : translation?.appointmentTime
                                    }
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    type="typography"
                                    label="Appointment Length (Min)"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={translation?.appointmentLength}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    type="typography"
                                    label="Appointment Address"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={translation?.appointmentAddress ? translation.appointmentAddress : '-'}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    type="typography"
                                    label="Appointment Contact Number"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={
                                        translation?.appointmentContactNumber
                                            ? translation.appointmentContactNumber
                                            : '-'
                                    }
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    type="typography"
                                    label="Interpretation Type"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={
                                        translation?.interpretationTypeId
                                            ? props.interpretationTypes?.find(
                                                  (x) =>
                                                      x.interpretationTypeId === translation.interpretationTypeId,
                                              )?.interpretationTypeName ?? '-'
                                            : '-'
                                    }
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    type="typography"
                                    label="Translation Type"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={translation?.translationType ? translation.translationType : '-'}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};
